import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaCar, FaCheckCircle, FaDollarSign, FaHandshake,
  FaShieldAlt, FaBolt, FaStar, FaGoogle
} from 'react-icons/fa';
import { contactAPI, vehicleAPI, resolveMediaUrl } from '../services/api';
import { toast } from 'react-toastify';
import bg1 from '../images/background-1.jpg';
import './Home.css';

const Home = () => {
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [submittingContact, setSubmittingContact] = useState(false);

  useEffect(() => {
    document.title = 'Kariyapperuma Car Sales | Premium Japanese Vehicle Imports';
    return () => { document.title = 'Kariyapperuma Car Sales'; };
  }, []);

  useEffect(() => {
    loadFeaturedVehicles();
  }, []);

  const loadFeaturedVehicles = async () => {
    try {
      // Try getting featured vehicles first, fallback to all vehicles
      let response = await vehicleAPI.getFeatured();
      console.log('Featured API Response:', response);
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const vehicles = response.data.data.slice(0, 3);
        console.log('Featured vehicles loaded:', vehicles.length, vehicles);
        setFeaturedVehicles(vehicles);
      } else {
        console.warn('Featured endpoint returned no data, trying all vehicles');
        // Fallback: get all vehicles
        response = await vehicleAPI.getAll();
        if (response.data.success && response.data.data) {
          const vehicles = response.data.data.slice(0, 3);
          console.log('Loaded from all vehicles:', vehicles.length, vehicles);
          setFeaturedVehicles(vehicles);
        }
      }
    } catch (error) {
      console.error('Error loading vehicles:', error);
      setFeaturedVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (!contactForm.name.trim() || !contactForm.phone.trim() || !contactForm.email.trim()) {
      toast.error('Please fill in your name, phone number, and email.');
      return;
    }

    setSubmittingContact(true);
    try {
      const response = await contactAPI.submit({
        name: contactForm.name.trim(),
        phone: contactForm.phone.trim(),
        email: contactForm.email.trim(),
        message: contactForm.message.trim()
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setContactForm({ name: '', phone: '', email: '', message: '' });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send message. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSubmittingContact(false);
    }
  };

  const handleInputChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const formatPrice = (price) => {
    if (price >= 1000000) {
      const millions = parseFloat((price / 1000000).toFixed(2));
      return `Rs. ${millions} Million`;
    }
    return `Rs. ${Number(price).toLocaleString('en-LK')}`;
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section
        className="hero-section"
        style={{
          backgroundImage: `url(${bg1})`
        }}
      >
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-text"
          >
            {/* Hero Badge */}
            <motion.span
              className="hero-badge"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              🏆 Premium Japanese Vehicle Imports
            </motion.span>

            {/* Hero Title */}
            <h1 className="hero-title">
              High Quality Unregistered Car Importer
            </h1>

            {/* Hero Subtitle */}
            <p className="hero-subtitle">
             Premium Japanese Import Vehicles.
             Serving Sri Lanka with trust for over 25+ years and 1000+ satisfied customers. </p>

            {/* CTA Buttons */}
            <div className="hero-buttons">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <Link to="/vehicles" className="btn btn-primary btn-lg">
                  <FaCar /> Browse Vehicles
                </Link>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <Link to="/contact" className="btn btn-outline btn-lg">
                  <span>📞</span> Schedule Call
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="trust-section">
        <div className="container">
          <div className="trust-grid">
            <motion.div 
              className="trust-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="trust-number">25+</div>
              <div className="trust-label">Years in Business</div>
            </motion.div>
            <motion.div 
              className="trust-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="trust-number">1000+</div>
              <div className="trust-label">Happy Customers</div>
            </motion.div>
            <motion.div 
              className="trust-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="trust-number">100%</div>
              <div className="trust-label">Quality Assured</div>
            </motion.div>
            <motion.div 
              className="trust-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="trust-number">24/7</div>
              <div className="trust-label">Customer Support</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">WHY CHOOSE US</span>
            <h2 className="section-title">Your Trusted Car Import Partner</h2>
            <p className="section-subtitle">
              Kariyapperuma Car Sales has been Sri Lanka's trusted source for high-quality 
              unregistered vehicle imports since 2000.
            </p>
          </div>

          <div className="features-grid">
            {[
              { icon: <FaCar />, title: 'Premium Selection', desc: 'Carefully curated collection of Japanese imports.' },
              { icon: <FaCheckCircle />, title: 'Quality Inspection', desc: 'Every vehicle thoroughly inspected before delivery.' },
              { icon: <FaDollarSign />, title: 'Best Value Pricing', desc: 'Competitive prices without compromising quality.' },
              { icon: <FaHandshake />, title: 'Expert Guidance', desc: 'Professional team with years of experience.' },
              { icon: <FaShieldAlt />, title: 'Complete Documentation', desc: 'We handle all import paperwork hassle-free.' },
              { icon: <FaBolt />, title: 'Fast Processing', desc: 'Efficient import and delivery process.' }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      <section className="featured-section section bg-light">
        <div className="container">
          <div className="section-header">
            <span className="section-label">OUR INVENTORY</span>
            <h2 className="section-title">Featured Vehicles</h2>
            <p className="section-subtitle">
              Check out our hand-picked selection of premium vehicles
            </p>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loader"></div>
            </div>
          ) : (
   <div className="featured-grid">
  {featuredVehicles.map((vehicle, index) => (
    <motion.div
      key={vehicle.id}
      className="vehicle-card"
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
    >
      {vehicle.badge && (
        <span className="vehicle-badge">{vehicle.badge}</span>
      )}
      
      {/* Vehicle Image - Using background-image */}
      {vehicle.images && vehicle.images.length > 0 && vehicle.images[0] ? (
        <div
          className="vehicle-image"
          style={{
            backgroundImage: `url(${resolveMediaUrl(vehicle.images[0])})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
          aria-label={`${vehicle.make} ${vehicle.model}`}
        />
      ) : (
        <div className="vehicle-image">
          <div className="vehicle-placeholder">
            <FaCar />
            <span>No Image Available</span>
          </div>
        </div>
      )}
      
      <div className="vehicle-info">
        <h3>{vehicle.make} {vehicle.model}</h3>
        <div className="vehicle-specs">
          <span className="spec-badge">🔧 {vehicle.fuelType}</span>
          <span className="spec-badge">📅 {vehicle.year}</span>
          <span className="spec-badge">⚙️ {vehicle.transmission}</span>
        </div>
        <div className="vehicle-price">{formatPrice(vehicle.price)}</div>
        <Link
          to={`/vehicles/${vehicle.id}`}
          className="btn btn-primary btn-block"
        >
          View Details
        </Link>
      </div>
    </motion.div>
  ))}
</div>
            
          )}

          <div className="text-center mt-5">
            <Link to="/vehicles" className="btn btn-outline btn-lg">
              View All Vehicles
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">TESTIMONIALS</span>
            <h2 className="section-title">What Our Customers Say</h2>
            <p className="section-subtitle">
              Real reviews from our satisfied customers on Google
            </p>
          </div>

          <div className="testimonials-grid">
            {[
              {
                name: 'Sarangan Balachandran',
                text: 'I recently bought my brand-new Toyota Yaris Ativ from Kariyapperuma Car Sales, and I\'m beyond happy with both the car and the experience! From the very first visit, Mr. Ranil impressed me with his professionalism, honesty, and outstanding customer service.',
                rating: 5
              },
              {
                name: 'Chamara Wannisekara',
                text: 'Splendid service. I recently experienced the iconic and value added service given by kariyapperuma car sales. Being a professioner; an attorney at law mr. Kariyapperuma proved that this business is not only just a car sale.',
                rating: 5
              },
              {
                name: 'Avesha Perera',
                text: 'Can recomend this place to anyone. Perfect conditioned, highest auction grade and genuine cars available. Customer care and customer service is superb and quick response. Also the prices are cheaper when comparing with other car dealers',
                rating: 5
              },
              {
                name: 'Samitha Hewawasam',
                text: 'I brought a new car after government liftoff the ban of vehicle imports. Excellent service in terms of document stuff and trusting',
                rating: 5
              },
              {
                name: 'Shavindra Manathunga',
                text: 'I bought Honda Grace from here. I definitely recommend to everyone who wishes to buy a car. I\'m really happy with the friendly customer service and flexibility. I also highly appreciate the trustworthiness.',
                rating: 5
              },
              {
                name: 'Kamal Gunarathne',
                text: 'I bought my dream vehicle from here. Most reasonable price (cheapest in the region) and genuinely good vehicles. Peace of mind as the whole process is transparent. Easy and pleasant transaction.',
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">- {testimonial.name}</div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-4">
            <a
              href="https://www.google.com/search?q=kariyapperuma+car+sales&rlz=1C1FHFK_en-GBLK1095LK1095#lrd=0x3ae2f8f571880af9:0x7be380b4db3eca34,1"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline btn-lg"
            >
              <FaGoogle /> View All Google Reviews
            </a>
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="quick-contact-section section bg-light">
        <div className="container">
          <div className="quick-contact-container">
            <div className="quick-contact-content">
              <h2>Ready to Find Your Perfect Car?</h2>
              <p>Contact us today and let our experts help you choose the right vehicle for your needs.</p>
              <div className="contact-info-quick">
                <div className="contact-item-quick">
                  <span>📞</span>
                  <span>+94 70 735 2969</span>
                </div>
                <div className="contact-item-quick">
                  <span>📍</span>
                  <span>600/4A, Kandy Road, Eldeniya, Kadawatha </span>
                </div>
              </div>
            </div>
            <form className="quick-contact-form" onSubmit={handleContactSubmit} autoComplete="off">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={contactForm.name}
                onChange={handleInputChange}
                required
                className="form-input"
                autoComplete="off"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={contactForm.phone}
                onChange={handleInputChange}
                required
                className="form-input"
                autoComplete="off"
              />
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={contactForm.email}
                onChange={handleInputChange}
                required
                className="form-input"
                autoComplete="off"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                value={contactForm.message}
                onChange={handleInputChange}
                rows="4"
                className="form-textarea"
              ></textarea>
              <button
                type="submit"
                className="btn btn-primary btn-block btn-lg"
                disabled={submittingContact}
              >
                {submittingContact ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
